import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import unionBy from 'lodash/unionBy';
import moment from 'moment';
import { useAction } from 'hooks';
import * as selectors from '../../selectors';
import * as actions from '../../actions';
import { getAlertName, transformSystemAlerts } from './helpers';
export const useSystemAlerts = (isGlobal)=>{
    if (!isGlobal) {
        return {
            systemAlerts: [],
            onCloseSystemAlert: undefined
        };
    }
    const newSystemAlerts = useSelector(selectors.systemAlerts);
    const systemAlertsSettings = useSelector(selectors.systemAlertsSettings);
    const localizationResources = useSelector(selectors.localizationResources);
    const localizationResourcesRef = useRef(localizationResources);
    const systemAlertsSettingsRef = useRef(systemAlertsSettings);
    const dismissSystemAlert = useAction(actions.dismissSystemAlert);
    const [systemAlerts, setSystemAlerts] = useState([]);
    const onDisplayAlerts = debounce(useAction(actions.displayAlerts), 150);
    const onCloseSystemAlert = (alert)=>{
        const { id } = alert;
        setSystemAlerts(systemAlerts.filter((a)=>a.id !== id));
        dismissSystemAlert(alert);
    };
    const onRefreshAlerts = (forceUpdate = false)=>{
        let unionSystemAlerts = [];
        unionSystemAlerts = unionBy([
            ...systemAlerts
        ].reverse(), newSystemAlerts, (systemAlert)=>`${systemAlert.alertId}_${systemAlert.createTimestamp}`);
        if (!forceUpdate && unionSystemAlerts.length <= systemAlerts.length) {
            return;
        }
        unionSystemAlerts = transformSystemAlerts(unionSystemAlerts, systemAlertsSettings, localizationResources).filter((systemAlert)=>systemAlert && systemAlert.message).reverse();
        setSystemAlerts(unionSystemAlerts);
        onDisplayAlerts();
    };
    useEffect(()=>{
        if (newSystemAlerts.length) {
            const forceUpdate = localizationResourcesRef.current !== localizationResources || systemAlertsSettingsRef.current !== systemAlertsSettings;
            onRefreshAlerts(forceUpdate);
        }
        localizationResourcesRef.current = localizationResources;
        systemAlertsSettingsRef.current = systemAlertsSettings;
    }, [
        newSystemAlerts,
        localizationResources,
        systemAlertsSettings
    ]);
    return {
        systemAlerts,
        onCloseSystemAlert
    };
};
export const useAlerts = (isGlobal, activeClinicMembership)=>{
    const { clinicHcpMembershipId } = activeClinicMembership || {};
    const newAlerts = useSelector(selectors.alerts);
    const newAlertsTimeThreshold = useSelector(selectors.alertsTimeThreshold);
    const dismissedAlerts = useSelector(selectors.dismissedAlerts);
    const openModalId = useSelector(selectors.modal);
    const route = useSelector(selectors.route);
    const alertsTimeThreshold = useRef(+moment.utc().locale('en').format('X'));
    const prevRouteRef = useRef(null);
    const prevClinicHcpMembershipIdRef = useRef(clinicHcpMembershipId);
    const [alerts, setAlerts] = useState([]);
    const dismissAlert = useAction(actions.dismissAlert);
    const onDisplayAlerts = debounce(useAction(actions.displayAlerts), 150);
    const onCloseAlert = (alert)=>{
        const { id } = alert;
        setAlerts(alerts.filter((a)=>a.id !== id));
        if (alert.attributes && alert.attributes.isPinned) {
            dismissAlert(alert);
        }
    };
    useEffect(()=>{
        let currentAlerts = alerts;
        if (prevRouteRef.current && route !== prevRouteRef.current) {
            if (prevClinicHcpMembershipIdRef.current !== clinicHcpMembershipId) {
                currentAlerts = [];
            } else {
                currentAlerts = filter(alerts, (alert)=>alert.attributes && alert.attributes.isPinned);
            }
        }
        if (newAlerts.length || newAlertsTimeThreshold > alertsTimeThreshold.current) {
            const localAlerts = filter(newAlerts, (alert)=>{
                const alertName = getAlertName(alert);
                return !includes(dismissedAlerts, alertName) && (!openModalId || openModalId && (isGlobal && alert.isGlobal || !isGlobal && !alert.isGlobal));
            });
            let unionAlerts = unionBy([
                ...currentAlerts
            ].reverse(), localAlerts, (alert)=>getAlertName(alert));
            unionAlerts = filter(unionAlerts, (alert)=>alert.timestamp >= alertsTimeThreshold.current).reverse();
            setAlerts(unionAlerts);
            alertsTimeThreshold.current = newAlertsTimeThreshold;
            prevRouteRef.current = route;
            onDisplayAlerts();
        } else {
            setAlerts(currentAlerts);
        }
    }, [
        newAlerts,
        newAlertsTimeThreshold,
        route,
        activeClinicMembership
    ]);
    return {
        alerts,
        onCloseAlert
    };
};
